import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["banner", "modal"];

  // Store the element that triggered the modal
  triggerElement = null;

  connect() {
    this.handleEscapeBound = this.handleEscape.bind(this);
    this.handleOutsideClickBound = this.handleOutsideClick.bind(this);
    this.handleFocusTrapBound = this.handleFocusTrap.bind(this); // Bind focus trap handler
    this.handleScrollBound = this.handleScroll.bind(this);

    // Check for valid consent
    try {
      const consent = localStorage.getItem("cookieConsent");
      if (consent) {
        const preferences = JSON.parse(consent);
        if (preferences && typeof preferences === "object") {
          // Consent already given, hide banner logic handled by CSS/initial script
          return;
        }
      }
      // No valid consent found, ensure banner is potentially visible
      document.documentElement.classList.add("show-cookie-consent");

      // Add scroll listener for delayed banner appearance
      window.addEventListener("scroll", this.handleScrollBound, {
        passive: true,
      });
    } catch (e) {
      localStorage.removeItem("cookieConsent");
      document.documentElement.classList.add("show-cookie-consent");

      // Add scroll listener for delayed banner appearance
      window.addEventListener("scroll", this.handleScrollBound, {
        passive: true,
      });
    }
  }

  disconnect() {
    this.removeEventListeners();
    document.removeEventListener("keydown", this.handleFocusTrapBound); // Clean up focus trap listener
    window.removeEventListener("scroll", this.handleScrollBound);
  }

  customize(event) {
    event.preventDefault();
    event.stopPropagation();

    // Store the trigger element
    this.triggerElement = event.currentTarget;

    this.modalTarget.classList.remove("hidden");
    // Make modal programmatically visible for screen readers AFTER display is updated
    this.modalTarget.removeAttribute("aria-hidden");
    document.body.classList.add("overflow-hidden");
    this.addEventListeners();
    this.loadSavedPreferences();

    // Focus management
    this.focusFirstElementInModal();
    document.addEventListener("keydown", this.handleFocusTrapBound);
  }

  closeModal(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.modalTarget.classList.add("hidden");
    // Hide modal programmatically BEFORE display is updated
    this.modalTarget.setAttribute("aria-hidden", "true");
    document.body.classList.remove("overflow-hidden");
    this.removeEventListeners();
    document.removeEventListener("keydown", this.handleFocusTrapBound); // Remove focus trap listener

    // Return focus to the trigger element if it exists
    if (this.triggerElement) {
      this.triggerElement.focus();
      this.triggerElement = null; // Clear trigger element
    }
  }

  addEventListeners() {
    document.addEventListener("keydown", this.handleEscapeBound);
    this.modalTarget.addEventListener("click", this.handleOutsideClickBound);
  }

  removeEventListeners() {
    document.removeEventListener("keydown", this.handleEscapeBound);
    this.modalTarget.removeEventListener("click", this.handleOutsideClickBound);
  }

  handleEscape(event) {
    if (
      event.key === "Escape" &&
      !this.modalTarget.classList.contains("hidden")
    ) {
      this.closeModal(event);
    }
  }

  handleOutsideClick(event) {
    // Only close if the click is directly on the modal backdrop (the modalTarget itself)
    if (event.target === this.modalTarget) {
      this.closeModal(event);
    }
  }

  // --- Scroll Handling ---

  handleScroll() {
    // Trigger banner animation after user scrolls 150 pixels
    if (window.scrollY > 150) {
      document.documentElement.classList.add("scrolled");
      // Remove scroll listener once triggered to improve performance
      window.removeEventListener("scroll", this.handleScrollBound);
    }
  }

  // --- Focus Management ---

  getFocusableElements() {
    if (!this.modalTarget) return [];
    // Standard focusable elements selector
    const focusableSelector =
      'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
    return Array.from(
      this.modalTarget.querySelectorAll(focusableSelector)
    ).filter(
      // Ensure elements are visible and not disabled
      (el) => !el.disabled && el.offsetWidth > 0 && el.offsetHeight > 0
    );
  }

  focusFirstElementInModal() {
    const focusableElements = this.getFocusableElements();
    if (focusableElements.length > 0) {
      // Delay focus slightly to ensure modal is fully rendered
      requestAnimationFrame(() => {
        focusableElements[0].focus();
      });
    }
  }

  handleFocusTrap(event) {
    if (event.key !== "Tab" || this.modalTarget.classList.contains("hidden")) {
      return; // Ignore if not Tab key or modal is hidden
    }

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault(); // No focusable elements, prevent tabbing
      return;
    }

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];
    const currentElement = document.activeElement;

    if (event.shiftKey) {
      // Shift + Tab
      if (currentElement === firstElement) {
        event.preventDefault();
        lastElement.focus(); // Wrap to last element
      }
    } else {
      // Tab
      if (currentElement === lastElement) {
        event.preventDefault();
        firstElement.focus(); // Wrap to first element
      }
    }
    // If tabbing within the modal, allow default behavior
  }

  // --- Rest of the methods (acceptAll, toggleAnalytics, etc.) ---

  acceptAll(event) {
    event.preventDefault();
    event.stopPropagation();
    this.setConsent({
      essential: true,
      analytics: true,
      marketing: true,
    });
    document.documentElement.classList.remove("show-cookie-consent");
    document.documentElement.classList.remove("scrolled");
    // Remove scroll listener if still active
    window.removeEventListener("scroll", this.handleScrollBound);
    if (!this.modalTarget.classList.contains("hidden")) {
      // Pass the event if available, otherwise null
      this.closeModal(event ? event : null);
    }
  }

  toggleAnalytics(event) {
    event.stopPropagation();
    // Logic might be added later if needed, currently just prevents propagation
  }

  toggleMarketing(event) {
    event.stopPropagation();
    // Logic might be added later if needed, currently just prevents propagation
  }

  savePreferences(event) {
    event.preventDefault();
    event.stopPropagation();
    const analyticsCheckbox = document.getElementById("analytics-cookie");
    const marketingCheckbox = document.getElementById("marketing-cookie");

    const analytics = analyticsCheckbox ? analyticsCheckbox.checked : false;
    const marketing = marketingCheckbox ? marketingCheckbox.checked : false;

    this.setConsent({
      essential: true,
      analytics: analytics,
      marketing: marketing,
    });

    this.closeModal(event);
    document.documentElement.classList.remove("show-cookie-consent");
    document.documentElement.classList.remove("scrolled");
    // Remove scroll listener if still active
    window.removeEventListener("scroll", this.handleScrollBound);
  }

  hasConsent() {
    try {
      const consent = localStorage.getItem("cookieConsent");
      if (!consent) return false;
      const preferences = JSON.parse(consent);
      return (
        preferences &&
        typeof preferences === "object" &&
        Object.keys(preferences).length > 0
      );
    } catch (e) {
      localStorage.removeItem("cookieConsent");
      return false;
    }
  }

  setConsent(preferences) {
    try {
      localStorage.setItem("cookieConsent", JSON.stringify(preferences));
      this.applyConsentPreferences(preferences);
    } catch (e) {
      // Silently handle error
    }
  }

  applyConsentPreferences(preferences) {
    const safePreferences =
      preferences && typeof preferences === "object"
        ? preferences
        : { analytics: false, marketing: false };
    if (safePreferences.analytics) {
      this.enableAnalytics();
    } else {
      this.disableAnalytics();
    }
    if (safePreferences.marketing) {
      this.enableMarketing();
    } else {
      this.disableMarketing();
    }
  }

  loadSavedPreferences() {
    try {
      const consent = localStorage.getItem("cookieConsent");
      const preferences = consent
        ? JSON.parse(consent)
        : { essential: true, analytics: true, marketing: true };
      const analyticsCheckbox = document.getElementById("analytics-cookie");
      const marketingCheckbox = document.getElementById("marketing-cookie");
      if (analyticsCheckbox) {
        analyticsCheckbox.checked = preferences.analytics !== false;
      }
      if (marketingCheckbox) {
        marketingCheckbox.checked = preferences.marketing !== false;
      }
    } catch (e) {
      const analyticsCheckbox = document.getElementById("analytics-cookie");
      const marketingCheckbox = document.getElementById("marketing-cookie");
      if (analyticsCheckbox) analyticsCheckbox.checked = true;
      if (marketingCheckbox) marketingCheckbox.checked = true;
    }
  }

  enableAnalytics() {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: "granted" });
    }
  }
  disableAnalytics() {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", { analytics_storage: "denied" });
    }
  }
  enableMarketing() {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", { ad_storage: "granted" });
    }
  }
  disableMarketing() {
    if (typeof window.gtag === "function") {
      window.gtag("consent", "update", { ad_storage: "denied" });
    }
  }
}
